import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { Input } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { resetState } from '../../redux/AfrSlice';
import { resetConceptState } from '../../redux/ConceptSlice';
import { selectEntities } from '../../redux/EntitiesSlice';
import { EditEntityQuery, Entity, getSelectedEntity, loadEditEntity, loadEntity, resetEntityState, selectEntityStateStatus } from '../../redux/EntitySlice';
import { isDirty, setIsDirty, setTaxonomyType } from '../../redux/MiscSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion, getAllTaxonomyVersions, TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import { selectAuthorizedEntities, selectUser, UserEntityReference, UserRoleEnum } from '../../redux/UserSlice';
import utilityHelper from '../../utils/utilityHelper';

export interface EntitySelectorOptions {
    selectorTitle: string;
    taxonomyType: TaxonomyTypeEnum | null;
}
const EntitySelector = (props: EntitySelectorOptions) => {
    const history = useHistory();
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const isDirtyInfo: boolean = useAppSelector(isDirty);
    const location = useLocation();

    const defaultValue: { value: string; label: string; } = { value: '', label: 'Select...' };


    const userRole = user && user.userProfile ? user.userProfile.role : "ENTITY_USER";
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);

    const entityStateStatusSelector = useAppSelector(selectEntityStateStatus);

    var entities : Entity[] = [];
    if (userRole === UserRoleEnum.Admin) {
        entities = useAppSelector(selectEntities);
    } else {
        var entityAuthDuplicates = useAppSelector(selectAuthorizedEntities);

        for (var i = 0; i < entityAuthDuplicates.length; i++) {
            var foundEntity = entities.find((element: any) => element.code == entityAuthDuplicates[i].code);
            if (!foundEntity) {
                entities.push(entityAuthDuplicates[i]);
            }
        }

        entities = entities.sort((n1: Entity, n2: Entity) => n1.code.localeCompare(n2.code));

    }

    const selectedEntity: Entity | null = useAppSelector(getSelectedEntity);
    //const selectedCode = selectedEntity ? selectedEntity.code : "none";
    const [entityId, setEntityId] = React.useState<string>("none");
    const [greaterThanTenSelectItem, setGreaterThanTenSelectItem] = React.useState<{ value: string; label: string; }>(defaultValue);

    const options = entities.map((entity: any) => ({
        value: entity.code,
        label: entity.code + " - " + entity.name,
    }))

    const getOptions = function (tt: 'AFR' | 'COURT_REPORTING' | 'COURT_REPORTING_939' | 'COURT_REPORTING_29' | 'DEPARTMENT' | 'BASE' | null) {
        var result: any[] = [];
        if (!entities) {
            return result;
        }
        var filteredEntities: Entity[] = entities;
        switch (tt) {
            case 'COURT_REPORTING_29':
            case 'COURT_REPORTING':
            case 'COURT_REPORTING_939':
                filteredEntities = entities.filter((x: Entity) => x.code < '200000');
                break;

            case 'DEPARTMENT':
                filteredEntities = entities.filter((x: Entity) => x.code > '900000');
                break;

            default:
                filteredEntities = entities.filter((x: Entity) => x.code < '900000');
                break;
        }
        return filteredEntities.map((entity: Entity) => ({
            value: entity.code,
            label: entity.code + " - " + entity.name,
        }))

    }

    const resetEntityStateAndLoadData = function (entityCodeValue: string) {
        dispatch(resetEntityState({}));
        dispatch(resetState({}));
        dispatch(resetConceptState({}));
        if (versionInfo) {
            // we're operating in conjunction with the version selector
            var eQuery: EditEntityQuery = {
                entityId: entityCodeValue,
                taxonomyYear: versionInfo.year
            };
            dispatch(loadEditEntity(eQuery));
        } else {
            var eQ: EditEntityQuery = {
                entityId: entityCodeValue,
                taxonomyYear: -1
            }
            dispatch(loadEntity(eQ));
        }
        dispatch(setIsDirty({ IsDirty: false, Page: "" }));

    }


    const onRadioChangeValue = (event: { target: { value: string; }; }) => {
        resetEntityStateAndLoadData(event.target.value);
    }


    const onSelectChangeValue = (selectedItem: { label: string; value: string; }) => {

        resetEntityStateAndLoadData(selectedItem.value);


        if (!utilityHelper.isInvalidAFRRoute(location.pathname)) {
            switch (props.taxonomyType) {
                case "COURT_REPORTING_939":
                    if (userRole === "ADMIN") {
                        history.push("/939CourtReporting");
                    } else {
                        history.push("/939CourtReporting");
                    }
                    break;

                case "COURT_REPORTING_29":
                    if (userRole === "ADMIN") {
                        history.push("/29CourtReporting");
                    } else {
                        history.push("/29CourtReporting");
                    }
                    break;

                default:
                    if (userRole === "ADMIN") {
                        history.push("/EntityAFRManagementA");
                    } else {
                        history.push("/EntityAFRManagement");
                    }
                    break;
            }
        }

    }

    const entitiesMoreThan10Render = (< div >
        <Select
            value={greaterThanTenSelectItem}
            isSearchable={!isDirtyInfo}
            isDisabled={isDirtyInfo == true || entityStateStatusSelector != "idle"}
            onChange={(e: any) => {
                onSelectChangeValue(e);
                setGreaterThanTenSelectItem(e);
            }}
            options={getOptions(props.taxonomyType)}
        />
    </div >)

    const entitiesTenOrLessRender = (<div>
        <ul>
            {entities.map((entity: any) => (
                <li key={entity.code}>
                    <Input
                        type="radio"
                        value={entity.code}
                        name="entity-chooser"
                        onChange={onRadioChangeValue}
                        disabled={isDirtyInfo}
                        checked={entity.code == entityId} />
                    {entity.code + " - " + entity.name}

                </li>
            ))}
        </ul>
    </div>);

    let entityChooserRender = (<div></div>);

    if (entities.length > 0 &&
        entities.length <= 10) {
        entityChooserRender = entitiesTenOrLessRender;
    }
    else if (entities.length > 10) {
        entityChooserRender = entitiesMoreThan10Render;
    }

    React.useEffect(() => {
        dispatch(getAllTaxonomyVersions(props.taxonomyType));
    }, [props.taxonomyType])

    React.useEffect(() => {
        if (selectedEntity) {
            resetEntityStateAndLoadData(selectedEntity.code);
        }
    }, [versionInfo])

    React.useEffect(() => {

        // reset the states when destroying the component
        dispatch(setTaxonomyType(props.taxonomyType));
        return function cleanup() {
            dispatch(resetState({}));
            dispatch(resetEntityState({}));
            dispatch(resetConceptState({}));
        }
    }, []);

    React.useEffect(() => {
        if (selectedEntity) {
            let shouldSetSelectedEntity = false;

            if (entities.length <= 10) {

                if (selectedEntity.code != entityId) {
                    {
                        shouldSetSelectedEntity = true;
                    }

                } 
            } else if (entities.length > 10) {

                if (selectedEntity.code != greaterThanTenSelectItem.value) {
                    shouldSetSelectedEntity = true;
                }
            }

            if (shouldSetSelectedEntity == true) {
                var foundEntitySelection = options.find((element: any) => element.value == selectedEntity.code);

                if (foundEntitySelection) {
                    if (entities.length <= 10) {
                        setEntityId(selectedEntity.code);
                    } else if (entities.length > 10) {
                        setGreaterThanTenSelectItem(foundEntitySelection);
                    }
                }
            }
        }
    }, [selectedEntity])

    return (
        <React.Fragment>
            {entityChooserRender}
        </React.Fragment>
    )
}

export default EntitySelector;


